// Notification Coordinator - Global notification management
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import { EventBus } from "../events/EventBus";
import debugLogger from "../utils/debugLogger";

import {
  NotificationSeverity,
  NotificationEvent,
  NotificationPayload,
  NotificationState,
  NotificationConfig,
  NotificationMetrics,
  NotificationBatch,
  NotificationPosition,
  NotificationError,
} from "./types";

export class NotificationCoordinator {
  private notifications: Map<string, NotificationState> = new Map();
  private config: NotificationConfig;
  private eventBus: EventBus;
  private metrics: NotificationMetrics;
  private batchCounter: number = 0;

  constructor(config: Partial<NotificationConfig> = {}) {
    this.config = {
      defaultDuration: 5000,
      maxNotifications: 10,
      enableAutoHide: true,
      enableSound: false,
      enableVibration: false,
      position: NotificationPosition.TOP_RIGHT,
      ...config,
    };

    this.eventBus = new EventBus();
    this.metrics = {
      totalShown: 0,
      totalHidden: 0,
      totalCleared: 0,
      activeNotifications: 0,
      averageDuration: 0,
      errorCount: 0,
      successCount: 0,
      warningCount: 0,
      infoCount: 0,
    };

    this.setupEventHandlers();
    debugLogger.info("notifications", "Notification Coordinator initialized");
  }

  // ✅ NO MAGIC STRINGS: Use typed event names
  private setupEventHandlers(): void {
    // Note: We don't subscribe to our own events to avoid infinite loops
    // The coordinator only emits events, other components listen to them
    debugLogger.info(
      "notifications",
      "Notification Coordinator event handlers setup complete",
    );
  }

  public show(notification: NotificationPayload): string {
    try {
      this.validateNotification(notification);

      const id = notification.id || this.generateId();
      const duration = notification.duration ?? this.config.defaultDuration;

      const notificationState: NotificationState = {
        id,
        message: notification.message,
        severity: notification.severity,
        open: true,
        duration,
        action: notification.action,
        metadata: notification.metadata,
        timestamp: notification.timestamp || new Date(),
        timeoutId: undefined,
      };

      // Check if we've reached the maximum notifications
      if (this.notifications.size >= this.config.maxNotifications) {
        this.removeOldestNotification();
      }

      this.notifications.set(id, notificationState);
      this.updateMetrics(notification.severity, "show");

      // Set up auto-hide if enabled
      if (this.config.enableAutoHide && duration > 0) {
        const timeoutId = setTimeout(() => {
          this.hide(id);
        }, duration);
        notificationState.timeoutId = timeoutId;
      }

      // Emit the notification show event
      this.eventBus.emitGlobal(
        NotificationEvent.NOTIFICATION_SHOW,
        notificationState,
      );

      debugLogger.info("notifications", `Notification shown: ${id}`, {
        message: notification.message,
        severity: notification.severity,
      });

      return id;
    } catch (error) {
      this.handleError(error as Error);
      throw error;
    }
  }

  public hide(id: string): void {
    try {
      const notification = this.notifications.get(id);
      if (!notification) {
        throw new NotificationError(
          "NOTIFICATION_NOT_FOUND",
          `Notification with id ${id} not found`,
        );
      }

      this.clearTimeout(notification);
      notification.open = false;
      this.notifications.delete(id);
      this.updateMetrics(notification.severity, "hide");

      this.eventBus.emitGlobal(NotificationEvent.NOTIFICATION_HIDE, { id });

      debugLogger.info("notifications", `Notification hidden: ${id}`);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  public clear(): void {
    try {
      // Clear all timeouts
      this.notifications.forEach((notification) => {
        this.clearTimeout(notification);
      });

      const count = this.notifications.size;
      this.notifications.clear();
      this.metrics.totalCleared += count;
      this.metrics.activeNotifications = 0;

      this.eventBus.emitGlobal(NotificationEvent.NOTIFICATION_CLEAR, { count });

      debugLogger.info(
        "notifications",
        `All notifications cleared: ${count} notifications`,
      );
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  public update(id: string, updates: Partial<NotificationPayload>): void {
    try {
      const notification = this.notifications.get(id);
      if (!notification) {
        throw new NotificationError(
          "NOTIFICATION_NOT_FOUND",
          `Notification with id ${id} not found`,
        );
      }

      // Clear existing timeout if duration is being updated
      if (updates.duration !== undefined) {
        this.clearTimeout(notification);
      }

      // Update notification properties
      Object.assign(notification, {
        ...updates,
        timestamp: new Date(),
      });

      // Set up new timeout if duration was updated
      if (
        updates.duration !== undefined &&
        this.config.enableAutoHide &&
        updates.duration > 0
      ) {
        const timeoutId = setTimeout(() => {
          this.hide(id);
        }, updates.duration);
        notification.timeoutId = timeoutId;
      }

      this.eventBus.emitGlobal(
        NotificationEvent.NOTIFICATION_UPDATE,
        notification,
      );

      debugLogger.info("notifications", `Notification updated: ${id}`, updates);
    } catch (error) {
      this.handleError(error as Error);
    }
  }

  public showBatch(notifications: NotificationPayload[]): string {
    try {
      if (notifications.length === 0) {
        throw new NotificationError(
          "BATCH_EMPTY",
          "Cannot show empty notification batch",
        );
      }

      const batchId = `batch_${++this.batchCounter}`;
      const batch: NotificationBatch = {
        notifications,
        batchId,
        timestamp: new Date(),
      };

      // Show each notification in the batch
      notifications.forEach((notification) => {
        this.show(notification);
      });

      this.eventBus.emitGlobal(NotificationEvent.NOTIFICATION_BATCH, batch);

      debugLogger.info(
        "notifications",
        `Notification batch shown: ${batchId}`,
        {
          count: notifications.length,
        },
      );

      return batchId;
    } catch (error) {
      this.handleError(error as Error);
      throw error;
    }
  }

  public getNotification(id: string): NotificationState | undefined {
    return this.notifications.get(id);
  }

  public getAllNotifications(): NotificationState[] {
    return Array.from(this.notifications.values());
  }

  public getActiveNotifications(): NotificationState[] {
    return Array.from(this.notifications.values()).filter((n) => n.open);
  }

  public getMetrics(): NotificationMetrics {
    return { ...this.metrics };
  }

  public getConfig(): NotificationConfig {
    return { ...this.config };
  }

  public updateConfig(updates: Partial<NotificationConfig>): void {
    this.config = { ...this.config, ...updates };
    debugLogger.info("notifications", "Configuration updated", updates);
  }

  public getEventBus(): EventBus {
    return this.eventBus;
  }

  private validateNotification(notification: NotificationPayload): void {
    if (!Object.values(NotificationSeverity).includes(notification.severity)) {
      throw new NotificationError(
        "INVALID_SEVERITY",
        `Invalid severity: ${notification.severity}`,
      );
    }

    if (notification.duration !== undefined && notification.duration < 0) {
      throw new NotificationError(
        "INVALID_DURATION",
        `Invalid duration: ${notification.duration}`,
      );
    }

    if (!notification.message || notification.message.trim() === "") {
      throw new NotificationError(
        "INVALID_SEVERITY",
        "Notification message cannot be empty",
      );
    }
  }

  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private removeOldestNotification(): void {
    const oldest = Array.from(this.notifications.values()).sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    )[0];

    if (oldest) {
      this.hide(oldest.id);
    }
  }

  private clearTimeout(notification: NotificationState): void {
    if (notification.timeoutId) {
      clearTimeout(notification.timeoutId);
      notification.timeoutId = undefined;
    }
  }

  private updateMetrics(
    severity: NotificationSeverity,
    action: "show" | "hide",
  ): void {
    if (action === "show") {
      this.metrics.totalShown++;
      this.metrics.activeNotifications++;

      switch (severity) {
        case NotificationSeverity.ERROR:
          this.metrics.errorCount++;
          break;
        case NotificationSeverity.SUCCESS:
          this.metrics.successCount++;
          break;
        case NotificationSeverity.WARNING:
          this.metrics.warningCount++;
          break;
        case NotificationSeverity.INFO:
          this.metrics.infoCount++;
          break;
      }
    } else if (action === "hide") {
      this.metrics.totalHidden++;
      this.metrics.activeNotifications = Math.max(
        0,
        this.metrics.activeNotifications - 1,
      );
    }

    // Update average duration
    const notifications = Array.from(this.notifications.values());
    if (notifications.length > 0) {
      const totalDuration = notifications.reduce(
        (sum, n) => sum + n.duration,
        0,
      );
      this.metrics.averageDuration = totalDuration / notifications.length;
    }
  }

  private handleError(error: Error): void {
    debugLogger.error("notifications", "Notification Coordinator error", error);
    this.metrics.errorCount++;
  }
}
